import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input, Label } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import { FileText, Plus, ArrowUpRight, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ framework_code: "IFRS-S2", fiscal_year: 2024, title: "" });

  const load = async () => {
    const { data } = await api.get("/reports");
    setReports(data.reports);
  };
  useEffect(() => {
    load();
    api.get("/tenants/frameworks").then((r) => setFrameworks(r.data.frameworks));
  }, []);

  const create = async () => {
    try {
      const { data } = await api.post("/reports", { ...form, fiscal_year: parseInt(form.fiscal_year) });
      toast.success("Draft report generated");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-1">Module</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Reporting Hub</h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
            Multi-framework, multi-standard. IFRS S2, SGX Core Metrics, GRI — AI-drafted, human-reviewed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/report-studio"><Button variant="outline" data-testid="reports-open-studio"><Sparkles className="h-4 w-4" /> Report Studio</Button></Link>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="primary" data-testid="new-report-btn"><Plus className="h-4 w-4" /> New Draft</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Generate report draft</DialogTitle>
              <DialogDescription>AI assembles sections from your data and material KPIs.</DialogDescription>
            </DialogHeader>
            <div>
              <Label>Framework</Label>
              <Select value={form.framework_code} onValueChange={(v) => setForm({...form, framework_code: v})}>
                <SelectTrigger data-testid="report-framework"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {frameworks.map((f) => <SelectItem key={f.code} value={f.code}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fiscal year</Label>
              <Input type="number" value={form.fiscal_year} onChange={(e) => setForm({...form, fiscal_year: e.target.value})} data-testid="report-year" />
            </div>
            <div>
              <Label>Title (optional)</Label>
              <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Sustainability Report FY2024" data-testid="report-title" />
            </div>
            <Button variant="primary" onClick={create} data-testid="report-submit-btn">Generate draft</Button>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {reports.map((r) => (
          <Link to={`/reports/${r.id}`} key={r.id} className="card-flat p-6 hover:bg-surface transition-colors group" data-testid="report-card">
            <div className="flex items-start justify-between mb-4">
              <div className="h-10 w-10 bg-accent/10 border border-accent/30 rounded-sm flex items-center justify-center">
                <FileText className="h-5 w-5 text-accent" />
              </div>
              <Badge variant={r.status === "draft" ? "warning" : "success"}>{r.status}</Badge>
            </div>
            <h3 className="font-display font-semibold text-lg mb-1 group-hover:text-accent transition-colors">{r.title}</h3>
            <div className="text-xs font-mono text-muted-foreground space-y-1 mt-3">
              <div>{r.framework_name}</div>
              <div>FY{r.fiscal_year}</div>
              {r.emissions_summary && (
                <div className="pt-2 mt-2 border-t border-border text-foreground">
                  S1+2: <span className="font-bold">{(r.emissions_summary.scope1 + r.emissions_summary.scope2).toFixed(1)}</span> tCO2e
                </div>
              )}
            </div>
            <div className="mt-4 flex items-center gap-1 text-xs text-accent font-medium">
              Open report <ArrowUpRight className="h-3 w-3" />
            </div>
          </Link>
        ))}
        {reports.length === 0 && (
          <div className="md:col-span-2 card-flat p-12 text-center text-muted-foreground">
            <FileText className="h-10 w-10 mx-auto mb-3 opacity-30" />
            No reports yet. Generate your first draft.
          </div>
        )}
      </div>
    </div>
  );
}
